import React, { ChangeEvent, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateUserNotificationsConfig } from 'src/constants/actionTypes'
import { ChevronRightMinor, EmailNewsletterMajor } from '@shopify/polaris-icons'
import { Typography } from '@mui/material'
import { getUserNotificationConfig } from 'src/selectors/user'
import { IOSSwitch } from 'src/components/shared/IOSSwitch'
import { colors } from 'src/theme'
import { createAsyncAction } from 'src/utils/reduxUtils'

interface Props {
  isSelected?: boolean
  onClick?: () => void
}

const EmailNotificationsItemComponent = ({ isSelected = false, onClick }: Props) => {
  const dispatch = useDispatch()

  const userNotificationConfig = useSelector(getUserNotificationConfig)

  const [enabled, setEnabled] = useState(!!userNotificationConfig?.enabled)

  const handleChange = useCallback(
    async (event: ChangeEvent, checked: boolean) => {
      try {
        setEnabled(checked)

        createAsyncAction(dispatch, UpdateUserNotificationsConfig.request({ enabled: checked }))
      } catch (error) {
        setEnabled(!checked)
      }
    },
    [dispatch],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('[role="switch"]')) {
        return
      }
      onClick?.()
    },
    [onClick],
  )

  return (
    <div
      css={[
        tw`bg-white rounded-lg p-4 flex flex-row items-center justify-between cursor-pointer transition-colors hover:bg-[#F6F6F6]`,
        isSelected && tw`bg-[#F6F6F6]`,
        !isSelected && tw`border-0 border-b border-t border-solid border-[#EDEDED] rounded-none`,
      ]}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label="Email notifications"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      <div css={tw`flex flex-row items-center`}>
        <EmailNewsletterMajor css={tw`mr-4`} width={20} height={20} />
        <div>
          <Typography variant="h6" css={tw`mb-1`}>
            Email notifications
          </Typography>
          <Typography
            variant="body2"
            color={enabled ? colors.COLORS_GREEN_500 : colors.SHADES_GREY_400}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </div>
      </div>

      <div css={tw`flex flex-row items-center`}>
        <IOSSwitch
          checked={enabled}
          inputProps={{ 'aria-label': 'controlled' }}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
        />
        <ChevronRightMinor css={tw`ml-2`} width={20} height={20} fill={colors.SHADES_GREY_300} />
      </div>
    </div>
  )
}

export const EmailNotificationsItem = memo(EmailNotificationsItemComponent)
